import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useMemo } from "react";
import Header from "../../components/Header";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = parseFloat(value);
  return Number.isFinite(num)
    ? num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : value;
};

const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Parsed manually (not via Date) to avoid UTC/local timezone day-shift issues.
const parseDateParts = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || "");
  if (!match) return null;
  const [, year, month, day] = match;
  return { year: Number(year), month: Number(month), day: Number(day) };
};

const renderDate = (value) => {
  const parts = parseDateParts(value);
  if (!parts) return "—";

  const display = `${String(parts.day).padStart(2, "0")}-${MONTH_SHORT[parts.month - 1]}-${parts.year}`;
  const title = `${String(parts.day).padStart(2, "0")}-${String(parts.month).padStart(2, "0")}-${parts.year}`;

  return <span title={title}>{display}</span>;
};

function SalaryTable({
  salaries,
  total,
  currentPage,
  itemsPerPage,
  loading,
  searchTerm,
  onPageChange,
  onLimitChange,
  onSearch,
  onHistory,
  onIncrement,
  onAdjustment,
}) {
  const filteredSalaries = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return salaries;

    return salaries.filter((salary) => {
      const empCode = String(salary.emp_code || "").toLowerCase();
      const employeeName = String(salary.employee_name || "").toLowerCase();
      return empCode.includes(search) || employeeName.includes(search);
    });
  }, [salaries, searchTerm]);

  const totalPages = Math.ceil(total / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;

  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
    }
  };

  const pageNumbers = [];
  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i += 1) {
    pageNumbers.push(i);
  }

  return (
    <Box m="20px">
      <Header title="Salary Management" subtitle="HR Admin / Salary" />

      <div className="container-fluid mt-4 p-3 bg-white rounded shadow-sm">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div
            className="position-relative me-3 mb-2"
            style={{ flex: 1, minWidth: "200px" }}
          >
            <input
              type="text"
              placeholder="Search by employee code or name..."
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
              className="form-control"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => onSearch("")}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  border: "none",
                  background: "transparent",
                  fontSize: "16px",
                  cursor: "pointer",
                  color: "#999",
                }}
              >
                ×
              </button>
            )}
          </div>

          <div className="d-flex align-items-center mb-2">
            <label className="form-label me-2 mb-0 text-body">Items per page:</label>
            <select
              className="form-select"
              style={{ width: "120px" }}
              value={itemsPerPage}
              onChange={(e) => {
                onLimitChange(parseInt(e.target.value, 10));
                onPageChange(1);
              }}
            >
              {[5, 10, 20, 50].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-hover table-bordered align-middle text-center">
            <thead className="table-dark">
              <tr>
                <th>Sr. No.</th>
                <th>Employee Code</th>
                <th>Employee Name</th>
                <th>Previous Gross</th>
                <th>Increment</th>
                <th>New Gross</th>
                <th>Effective From</th>
                <th>Revision Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-muted">
                    Loading salaries...
                  </td>
                </tr>
              ) : filteredSalaries.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-muted">
                    No active salary records found.
                  </td>
                </tr>
              ) : (
                filteredSalaries.map((salary, index) => (
                  <tr key={salary.employee_id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{salary.emp_code || "—"}</td>
                    <td>{salary.employee_name || "—"}</td>
                    <td>{formatCurrency(salary.gross)}</td>
                    <td>{formatCurrency(salary.increment)}</td>
                    <td>{formatCurrency(salary.total_gross)}</td>
                    <td>{renderDate(salary.effective_from)}</td>
                    <td>{salary.revision_type || "—"}</td>
                    <td>
                      <span className="badge bg-success">
                        {salary.status || "ACTIVE"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-2 justify-content-center flex-wrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => onHistory(salary)}
                        >
                          History
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          onClick={() => onIncrement(salary)}
                        >
                          + Increment
                        </button>
                        {salary.revision_type === "INITIAL" && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => onAdjustment(salary)}
                          >
                            Adjust
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-body">
            Showing {filteredSalaries.length} of {total} employees
          </span>

          <div>
            <button
              className="btn btn-outline-secondary btn-sm me-1"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                className={`btn btn-sm me-1 ${
                  currentPage === pageNum ? "btn-primary" : "btn-outline-secondary"
                }`}
                onClick={() => goToPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Box>
  );
}

SalaryTable.propTypes = {
  salaries: PropTypes.array.isRequired,
  total: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  searchTerm: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onLimitChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onHistory: PropTypes.func.isRequired,
  onIncrement: PropTypes.func.isRequired,
  onAdjustment: PropTypes.func.isRequired,
};

export default SalaryTable;
